// Client-side wrapper around the omrImport Cloud Function. Uploads a PDF,
// returns the produced MusicXML as a string. Network/auth concerns isolated
// here so the React handler stays focused on UI state.
//
// Server contract (functions/index.js > omrImport):
//   POST  multipart/form-data, field "file" = PDF, Authorization: Bearer <id_token>
//   200   application/vnd.recordare.musicxml+xml  body = MusicXML
//   401   "Missing/invalid token"
//   403   "Pro subscription required"
//   413   "File exceeds 32 MB"
//   502   "OMR service error" (Cloud Run failed)
//   504   timeout

import { auth } from "../../firebase";

// The omrImport function lives at this fixed URL. If the project ID or
// region ever change, update here. (Could also read from REACT_APP_OMR_URL,
// but a hardcoded URL avoids a build-time env coupling for a prod-only path.)
const OMR_FUNCTION_URL =
  "https://us-central1-flowframe-dc5ab.cloudfunctions.net/omrImport";

export class OmrUploadError extends Error {
  constructor(message, status) {
    super(message);
    this.name = "OmrUploadError";
    this.status = status;
  }
}

function messageForStatus(status, body) {
  switch (status) {
    case 401:
      return "Your session expired. Please sign out and back in, then try again.";
    case 403:
      return body || "Sheet music import requires a Pro subscription.";
    case 413:
      return "PDF is too large (32 MB max). Try a lower-resolution scan or fewer pages.";
    case 502:
      return "The OMR service had an error. Please try again in a moment.";
    case 504:
      return "The OMR service timed out. Try a shorter score, or split it into pages.";
    default:
      return body || `Upload failed (status ${status}).`;
  }
}

/**
 * Upload a PDF and return the produced MusicXML string.
 *
 * @param {File} file
 * @returns {Promise<string>} MusicXML
 * @throws {OmrUploadError}
 */
export async function uploadPdfForOmr(file) {
  const user = auth.currentUser;
  if (!user) {
    throw new OmrUploadError(
      "Please sign in to import sheet music.",
      0
    );
  }

  let idToken;
  try {
    idToken = await user.getIdToken();
  } catch (err) {
    throw new OmrUploadError(
      "Could not refresh your sign-in. Please sign out and back in.",
      0
    );
  }

  const form = new FormData();
  form.append("file", file, file.name);

  let response;
  try {
    response = await fetch(OMR_FUNCTION_URL, {
      method: "POST",
      headers: { Authorization: `Bearer ${idToken}` },
      body: form,
    });
  } catch (err) {
    throw new OmrUploadError(
      "Network error reaching the OMR service. Check your connection and try again.",
      0
    );
  }

  if (!response.ok) {
    let body = "";
    try {
      body = await response.text();
    } catch {
      /* ignore */
    }
    throw new OmrUploadError(
      messageForStatus(response.status, body),
      response.status
    );
  }

  return response.text();
}
